/**
 * useContainerSize - ResizeObserver hook for responsive SVG/Canvas containers
 *
 * Returns a ref callback and current { width, height } of the observed element.
 * Used by TopologyViewer, ExecutionViewer DAG, and SecurityPosture heatmap.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface ContainerSize {
  width: number;
  height: number;
}

interface UseContainerSizeOptions {
  debounceMs?: number;
  defaultWidth?: number;
  defaultHeight?: number;
}

export function useContainerSize(options: UseContainerSizeOptions = {}) {
  const { debounceMs = 100, defaultWidth = 800, defaultHeight = 600 } = options;
  const [size, setSize] = useState<ContainerSize>({ width: defaultWidth, height: defaultHeight });
  const elementRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    elementRef.current = node;
    if (node) {
      const rect = node.getBoundingClientRect();
      setSize({ width: Math.floor(rect.width) || defaultWidth, height: Math.floor(rect.height) || defaultHeight });
    }
  }, [defaultWidth, defaultHeight]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setSize({ width: Math.floor(width), height: Math.floor(height) });
      }, debounceMs);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [debounceMs]);

  return { ref, width: size.width, height: size.height };
}

export type { ContainerSize, UseContainerSizeOptions };
